'use client';

import type React from 'react';
import { Component, type ReactNode } from 'react';

type Props = {
	children: ReactNode;
	fallback?: ReactNode;
};

type State = {
	hasError: boolean;
};

/**
 * Error boundary for upgrade prompt components.
 * Silently fails to prevent upgrade UI errors from crashing the entire app.
 * The upgrade prompts are non-critical - users can still use the app without them.
 */
export default class UpgradeErrorBoundary extends Component<Props, State> {
	constructor(props: Props) {
		super(props);
		this.state = { hasError: false };
	}

	static getDerivedStateFromError(): State {
		// Update state so the next render shows the fallback UI
		return { hasError: true };
	}

	componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
		// Log error for monitoring - in production this could go to a service like Sentry
		console.error('[UpgradeErrorBoundary] Upgrade UI crashed:', error, errorInfo);
	}

	render() {
		if (this.state.hasError) {
			// Silently fail - don't show upgrade prompts if there's an error
			// This is intentional: upgrade prompts are promotional, not critical
			return this.props.fallback ?? null;
		}

		return this.props.children;
	}
}
